import { AJUDA_ESTOQUE } from "@/lib/ajuda/dialogos/estoque";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { formatarQuantidade, quantidadeParaNumero } from "@/lib/quantidade";

const inputCls =
  "h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground";
const labelCls = "mb-1 block text-xs font-medium text-zinc-600 dark:text-muted-foreground";

/**
 * Inventário de um material: "contei e tenho X".
 *
 * Não edita o saldo — lança a diferença. O saldo continua sendo a soma dos lançamentos, e o
 * extrato continua explicando como se chegou nele. Editar direto pareceria mais simples e
 * destruiria exatamente a propriedade que faz alguém confiar no número.
 */
export function InformarSaldoDialog({
  materialLineId,
  onFechar,
}: {
  materialLineId: string | null;
  onFechar: () => void;
}) {
  const utils = trpc.useUtils();
  const [quantidade, setQuantidade] = useState("");
  const [notas, setNotas] = useState("");

  const extrato = trpc.estoque.extrato.useQuery(
    { materialLineId: materialLineId ?? "", take: 1, skip: 0 },
    { enabled: !!materialLineId },
  );

  useEffect(() => {
    setQuantidade("");
    setNotas("");
  }, [materialLineId]);

  const informar = trpc.estoque.informarSaldo.useMutation({
    onSuccess: (r) => {
      toast.success(r.delta === 0 ? "O saldo já era esse." : "Saldo atualizado.");
      utils.estoque.invalidate();
      onFechar();
    },
    onError: (e) => toast.error(e.message || "Não foi possível informar o saldo."),
  });

  const atual = extrato.data?.saldo ?? 0;
  const contado = quantidadeParaNumero(quantidade);
  const delta = quantidade.trim() ? contado - atual : 0;

  return (
    <Dialog open={!!materialLineId} onOpenChange={(v) => !v && onFechar()}>
      <DialogContent className="max-w-sm dark:bg-card">
        <DialogHeader>
          <DialogTitle ajuda={AJUDA_ESTOQUE.informarSaldo}>Informar saldo contado</DialogTitle>
          <DialogDescription className="dark:text-muted-foreground">
            {extrato.data?.material.name ?? "…"}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md bg-zinc-50 px-3 py-2 text-xs dark:bg-muted/40">
          <span className="text-zinc-600 dark:text-muted-foreground">O sistema tem </span>
          <strong className="tabular-nums text-zinc-900 dark:text-foreground">
            {formatarQuantidade(atual)}
            {extrato.data?.material.unit ? ` ${extrato.data.material.unit}` : ""}
          </strong>
        </div>

        <div>
          <p className={labelCls}>Quanto você contou</p>
          <input
            value={quantidade}
            onChange={(e) => setQuantidade(e.target.value)}
            inputMode="decimal"
            placeholder="0"
            autoFocus
            className={inputCls}
          />
          {quantidade.trim() && delta !== 0 && (
            <p className="mt-1 text-[11px] text-zinc-500 dark:text-muted-foreground">
              Vai lançar {delta > 0 ? "uma entrada" : "uma saída"} de{" "}
              <strong className="tabular-nums">{formatarQuantidade(Math.abs(delta))}</strong> para o saldo
              virar o que você contou.
            </p>
          )}
        </div>

        <div>
          <p className={labelCls}>Observação (opcional)</p>
          <input
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            placeholder="Ex: contagem de setembro"
            className={inputCls}
          />
        </div>

        <div className="flex justify-end gap-2 border-t border-zinc-100 pt-3 dark:border-border">
          <button
            type="button"
            onClick={onFechar}
            className="inline-flex h-9 items-center rounded-md border border-zinc-200 px-3 text-sm font-medium text-zinc-600 hover:bg-zinc-50 dark:border-border dark:text-muted-foreground"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={!quantidade.trim() || informar.isPending}
            onClick={() =>
              informar.mutate({
                materialLineId: materialLineId!,
                quantidade: contado,
                notes: notas.trim() || undefined,
              })
            }
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
          >
            {informar.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Confirmar
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
